import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
  FiSearch,
  FiX,
  FiUser,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiEye,
} from "react-icons/fi";
import {
  getWebTaskSessions,
  getWebTaskUserDetail,
  getWebTasks,
  resolveWebTaskLogoUrl,
} from "../../api/webTaskAdminApi";

const STATUS_META = {
  COMPLETED: { chip: "bg-green-50 text-green-700 border-green-200", label: "Completed" },
  TIMEOUT: { chip: "bg-amber-50 text-amber-700 border-amber-200", label: "Timeout" },
  INSUFFICIENT_PROGRESS: { chip: "bg-gray-100 text-gray-700 border-gray-200", label: "Exited Early" },
  BOT_CLICKS: { chip: "bg-red-50 text-red-700 border-red-200", label: "Bot Clicks" },
  CHEAT_SLEEP: { chip: "bg-red-50 text-red-700 border-red-200", label: "Cheat Sleep" },
  TIME_SPOOFED: { chip: "bg-red-50 text-red-700 border-red-200", label: "Time Spoofed" },
  STARTED: { chip: "bg-blue-50 text-blue-700 border-blue-200", label: "In Progress" },
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return String(dateStr);
  }
};

/* ---------------- User Detail Modal ---------------- */
const UserDetailModal = ({ userId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchUser = async () => {
      setLoading(true);
      try {
        const res = await getWebTaskUserDetail(userId);
        const data = res?.data || res || {};
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (!cancelled) toast.error(err.message || "Failed to load user data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUser();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const sessions = detail?.sessions || [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-xs" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-4 border border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
              <FiUser size={18} />
              {detail?.displayName ||
                detail?.name ||
                detail?.username ||
                detail?.user?.displayName ||
                detail?.user?.name ||
                detail?.user?.username ||
                (detail?.email ? detail.email.split("@")[0] : null) ||
                `User #${userId}`}
            </h3>
            <p className="text-xs text-gray-400">
              {(detail?.email || detail?.user?.email) && (
                <span className="font-mono text-gray-500 mr-2">
                  {detail.email || detail.user.email} ·
                </span>
              )}
              Web task transaction history &amp; total event points credited
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-black rounded-lg hover:bg-gray-100 transition cursor-pointer"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-center">
                  <div className="text-[10px] text-gray-400 uppercase font-bold">Total Sessions</div>
                  <div className="text-xl font-mono font-black text-gray-900 mt-1">
                    {detail?.totalSessions ?? 0}
                  </div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                  <div className="text-[10px] text-green-700 uppercase font-bold">Completed</div>
                  <div className="text-xl font-mono font-black text-green-700 mt-1">
                    {detail?.completedSessions ?? 0}
                  </div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
                  <div className="text-[10px] text-purple-700 uppercase font-bold">Total EP Earned</div>
                  <div className="text-xl font-mono font-black text-purple-700 mt-1">
                    +{detail?.totalEpEarned ?? 0} EP
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                  <div className="text-[10px] text-amber-800 uppercase font-bold">Event Rank</div>
                  <div className="text-xl font-mono font-black text-amber-900 mt-1">
                    {detail?.eventRank ? `#${detail.eventRank}` : "—"}
                  </div>
                </div>
              </div>

              {/* User Sessions Table */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-left text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 font-bold uppercase text-[10px]">
                    <tr>
                      <th className="px-3 py-2.5">Session ID</th>
                      <th className="px-3 py-2.5">Task</th>
                      <th className="px-3 py-2.5">Duration</th>
                      <th className="px-3 py-2.5">EP</th>
                      <th className="px-3 py-2.5">Status</th>
                      <th className="px-3 py-2.5">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 font-medium">
                    {sessions.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-6 text-gray-400">
                          No transactions found for this user
                        </td>
                      </tr>
                    ) : (
                      sessions.map((s) => {
                        const meta = STATUS_META[s.status] || { chip: "bg-gray-100 text-gray-600", label: s.status };
                        return (
                          <tr key={s.sessionId} className="hover:bg-gray-50">
                            <td className="px-3 py-2.5 font-mono text-[11px] text-gray-500">
                              {s.sessionId}
                            </td>
                            <td className="px-3 py-2.5 font-bold text-gray-900">{s.taskId}</td>
                            <td className="px-3 py-2.5 font-mono">{s.activeSeconds ?? 0}s</td>
                            <td className="px-3 py-2.5 font-mono font-bold text-purple-700">
                              {s.earnedEP > 0 ? `+${s.earnedEP} EP` : "0 EP"}
                            </td>
                            <td className="px-3 py-2.5">
                              <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded border ${meta.chip}`}>
                                {meta.label}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-gray-400 font-mono text-[11px]">
                              {formatDateTime(s.completedAt || s.startTime)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------------- Main Transaction History Component ---------------- */
export default function WebTaskTransactionHistory({
  initialTaskId = "",
}) {
  const [tasks, setTasks] = useState([]);
  const [taskId, setTaskId] = useState(initialTaskId);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("all"); // "today" | "7d" | "30d" | "all"

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [detailUserId, setDetailUserId] = useState(null);

  // Load task list for the filter dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await getWebTasks({ page: 1, limit: 100 });
        setTasks(res?.data?.webTasks || []);
      } catch (err) {
        console.warn("Could not load tasks dropdown:", err);
      }
    })();
  }, []);

  // Sync initialTaskId prop
  useEffect(() => {
    if (initialTaskId) {
      setTaskId(initialTaskId);
    }
  }, [initialTaskId]);

  const getDateRange = () => {
    const now = new Date();
    if (dateFilter === "today") {
      const todayStr = now.toISOString().slice(0, 10);
      return { from: todayStr, to: todayStr };
    }
    if (dateFilter === "7d") {
      const past = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return { from: past.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
    }
    if (dateFilter === "30d") {
      const past = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return { from: past.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
    }
    return {};
  };

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const range = getDateRange();
      const res = await getWebTaskSessions({
        page: currentPage,
        limit: 25,
        taskId: taskId || undefined,
        status: statusFilter || undefined,
        search: search || undefined,
        includeOpen: true,
        ...range,
      });

      const data = res?.data || res || {};
      setSessions(data.sessions || []);
      const pg = data.pagination || {};
      setTotalItems(Number(pg.totalItems || 0));
      setTotalPages(Number(pg.totalPages || 1));
    } catch (err) {
      console.error("Failed to load sessions:", err);
      toast.error(err.message || "Failed to load transaction history");
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, taskId, statusFilter, search, dateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchSessions();
  };

  const handleResetFilters = () => {
    setTaskId("");
    setStatusFilter("");
    setSearch("");
    setDateFilter("all");
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Filters Bar */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 shadow-xs space-y-3">
        <form onSubmit={handleSearchSubmit} className="flex flex-wrap items-center gap-3">
          {/* Search Input */}
          <div className="relative flex-1 min-w-[220px]">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by User, Email, or Session ID…"
              className="w-full bg-white border border-gray-300 rounded-xl pl-9 pr-3 py-2 text-xs outline-none focus:border-black focus:ring-1 focus:ring-black transition"
            />
          </div>

          {/* Task Dropdown Filter */}
          <select
            value={taskId}
            onChange={(e) => {
              setTaskId(e.target.value);
              setCurrentPage(1);
            }}
            className="bg-white border border-gray-300 rounded-xl px-3 py-2 text-xs outline-none focus:border-black cursor-pointer"
          >
            <option value="">All Web Tasks</option>
            {tasks.map((t) => (
              <option key={t.id || t.taskId} value={t.taskId}>
                {t.title || t.taskId}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="bg-white border border-gray-300 rounded-xl px-3 py-2 text-xs outline-none focus:border-black cursor-pointer"
          >
            <option value="">All Statuses</option>
            <option value="COMPLETED">Completed (Rewarded)</option>
            <option value="STARTED">In Progress</option>
            <option value="TIMEOUT">Timeout</option>
            <option value="INSUFFICIENT_PROGRESS">Exited Early</option>
            <option value="BOT_CLICKS">Bot Clicks</option>
            <option value="CHEAT_SLEEP">Cheat Sleep</option>
            <option value="TIME_SPOOFED">Time Spoofed</option>
          </select>

          {/* Date Filter */}
          <select
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="bg-white border border-gray-300 rounded-xl px-3 py-2 text-xs outline-none focus:border-black cursor-pointer font-medium"
          >
            <option value="all">All Time</option>
            <option value="today">Today Only</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>

          <button
            type="submit"
            className="px-4 py-2 bg-black text-[#ffff00] text-xs font-bold rounded-xl hover:bg-gray-800 transition cursor-pointer"
          >
            Filter
          </button>

          {(search || taskId || statusFilter || dateFilter !== "all") && (
            <button
              type="button"
              onClick={handleResetFilters}
              className="p-2 text-gray-400 hover:text-red-500 transition cursor-pointer"
              title="Reset Filters"
            >
              <FiX size={16} />
            </button>
          )}

          <button
            type="button"
            onClick={fetchSessions}
            disabled={loading}
            className="p-2 border border-gray-200 rounded-xl text-gray-500 hover:text-black hover:bg-gray-50 transition cursor-pointer disabled:opacity-50 ml-auto"
            title="Refresh Transactions"
          >
            <FiRefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </form>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-16">
            <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase font-bold text-[11px] tracking-wider">
                <tr>
                  <th className="px-4 py-3.5">Session ID</th>
                  <th className="px-4 py-3.5">User</th>
                  <th className="px-4 py-3.5">Task</th>
                  <th className="px-4 py-3.5">Activity</th>
                  <th className="px-4 py-3.5 text-center">Reward</th>
                  <th className="px-4 py-3.5 text-center">Status</th>
                  <th className="px-4 py-3.5">Date / Time</th>
                  <th className="px-4 py-3.5 text-right">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-medium">
                {sessions.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-12 text-center text-gray-400">
                      No web task transactions found.
                    </td>
                  </tr>
                ) : (
                  sessions.map((s) => {
                    const meta = STATUS_META[s.status] || { chip: "bg-gray-100 text-gray-700", label: s.status };
                    const user = s.user || {};
                    const userName =
                      s.displayName ||
                      s.name ||
                      s.username ||
                      user.displayName ||
                      user.name ||
                      user.username ||
                      (s.email ? s.email.split("@")[0] : null) ||
                      (user.email ? user.email.split("@")[0] : null) ||
                      `User #${s.userId}`;
                    const userEmail = s.email || user.email;
                    const logoSrc = resolveWebTaskLogoUrl(s.task?.logoUrl || s.taskLogo);
                    const taskTitle = s.task?.title || s.taskTitle || s.taskId;

                    return (
                      <tr key={s.sessionId} className="hover:bg-gray-50/80 transition">
                        {/* Session ID */}
                        <td className="px-4 py-3.5">
                          <span
                            className="font-mono text-[11px] text-gray-500 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded cursor-copy hover:border-black"
                            title="Click to copy Session ID"
                            onClick={() => {
                              navigator.clipboard.writeText(s.sessionId);
                              toast.success("Session ID copied");
                            }}
                          >
                            {s.sessionId}
                          </span>
                        </td>

                        {/* User */}
                        <td className="px-4 py-3.5">
                          <button
                            type="button"
                            onClick={() => setDetailUserId(s.userId)}
                            className="text-left group cursor-pointer"
                          >
                            <div className="font-bold text-gray-900 group-hover:underline flex items-center gap-1.5">
                              <span>{userName}</span>
                            </div>
                            {userEmail ? (
                              <div className="text-[10px] text-gray-400 truncate max-w-[150px]">
                                {userEmail}
                              </div>
                            ) : (
                              <div className="text-[10px] text-gray-400 font-mono">
                                ID #{s.userId}
                              </div>
                            )}
                          </button>
                        </td>

                        {/* Task */}
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            {logoSrc ? (
                              <img
                                src={logoSrc}
                                alt=""
                                className="w-6 h-6 rounded-md object-contain bg-gray-50 border border-gray-200 shrink-0"
                              />
                            ) : null}
                            <div className="truncate max-w-[140px]">
                              <div className="font-bold text-gray-800 truncate">
                                {taskTitle}
                              </div>
                              <div className="text-[10px] text-gray-400 font-mono truncate">
                                {s.taskId}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Activity */}
                        <td className="px-4 py-3.5 font-mono text-[11px] text-gray-600">
                          <div>⏱ {Number(s.activeSeconds || 0)}s active</div>
                          <div className="text-[10px] text-gray-400">
                            👆 {Number(s.clicks || 0)} clicks · 📄 {Number(s.pages || 0)} pages
                          </div>
                        </td>

                        {/* Reward */}
                        <td className="px-4 py-3.5 text-center font-mono font-black text-xs">
                          {Number(s.earnedEP || 0) > 0 ? (
                            <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-lg">
                              +{s.earnedEP} EP
                            </span>
                          ) : (
                            <span className="text-gray-400">0 EP</span>
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3.5 text-center">
                          <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-md border ${meta.chip}`}>
                            {meta.label}
                          </span>
                        </td>

                        {/* Date / Time */}
                        <td className="px-4 py-3.5 text-gray-500 font-mono text-[11px] whitespace-nowrap">
                          {formatDateTime(s.completedAt || s.startTime)}
                        </td>

                        {/* Details */}
                        <td className="px-4 py-3.5 text-right">
                          <button
                            type="button"
                            onClick={() => setDetailUserId(s.userId)}
                            className="p-1.5 text-gray-400 hover:text-black rounded-lg hover:bg-gray-100 transition cursor-pointer"
                            title="View User Details"
                          >
                            <FiEye size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 bg-gray-50 text-xs">
            <span className="text-gray-500">
              Showing <b>{sessions.length}</b> of <b>{totalItems.toLocaleString()}</b> transactions
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage <= 1 || loading}
                className="px-3 py-1.5 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 transition cursor-pointer font-semibold flex items-center gap-1"
              >
                <FiChevronLeft size={13} /> Previous
              </button>
              <span className="font-mono text-gray-600 px-2 font-bold">
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage >= totalPages || loading}
                className="px-3 py-1.5 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 transition cursor-pointer font-semibold flex items-center gap-1"
              >
                Next <FiChevronRight size={13} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {detailUserId && (
        <UserDetailModal
          userId={detailUserId}
          onClose={() => setDetailUserId(null)}
        />
      )}
    </div>
  );
}

